/**
 * Settings Panel
 * Right sidebar for editing section settings
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from '../../axiosConfig';
import Modal from '../../components/ui/Modal';

function SettingsPanel({ section, onUpdate, onClose, globalStyles, availableProducts = [] }) {
  const [settings, setSettings] = useState(section.settings || {});
  const [content, setContent] = useState(section.content || {});

  // The inspector stays mounted while merchants move between sections.
  // Keep its local form state in step with the newly selected section.
  useEffect(() => {
    setSettings(section.settings || {});
    setContent(section.content || {});
  }, [section.id, section.settings, section.content]);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onUpdate({ settings: newSettings });
  };

  const handleContentChange = (key, value) => {
    const newContent = { ...content, [key]: value };
    setContent(newContent);
    onUpdate({ content: newContent });
  };

  return (
    <div className="tb-settings-panel">
      <div className="tb-settings-panel__header">
        <h3>{getSectionTitle(section.type)}</h3>
        <button className="tb-settings-panel__close" onClick={onClose}>
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="tb-settings-panel__body">
        {/* Content Settings */}
        <div className="tb-settings-section">
          <h4 className="tb-settings-section__title">Content</h4>
          {renderContentFields(section.type, content, handleContentChange, availableProducts)}
        </div>

        {/* Layout Settings */}
        <div className="tb-settings-section">
          <h4 className="tb-settings-section__title">Layout</h4>
          {renderLayoutFields(section.type, settings, handleSettingChange)}
        </div>

        {/* Style Settings */}
        <div className="tb-settings-section">
          <h4 className="tb-settings-section__title">Style</h4>
          {renderStyleFields(section.type, settings, handleSettingChange)}
        </div>
      </div>
    </div>
  );
}

function getSectionTitle(type) {
  const titles = {
    'hero-banner': 'Hero Banner',
    'hero-slider': 'Hero Slider',
    'video-banner': 'Video Banner',
    'featured-products': 'Featured Products',
    'all-products': 'All Products',
    'categories-grid': 'Categories Grid',
    'testimonials': 'Testimonials',
    'newsletter': 'Newsletter',
    'text-image': 'Text + Image',
    'trust-badges': 'Trust Badges',
    'announcement-bar': 'Announcement Bar',
    'ad-placement': 'Ad Placement',
    'edition-hero': 'Edition Hero',
    'edition-category-mosaic': 'Collection Mosaic',
    'edition-product-edit': 'Product Edit',
    'edition-story': 'The Edit',
    'atelier-hero': 'Atelier Welcome',
    'atelier-category-mosaic': 'Rituals Mosaic',
    'atelier-services': 'Services Feature',
    'atelier-bestsellers': 'Bestsellers',
    'atelier-story': 'Ingredient Story',
    'gallery-hero': 'Gallery Introduction',
    'gallery-collections': 'Collection Wall',
    'gallery-featured-works': 'Featured Works',
    'gallery-statement': 'Gallery Statement',
    'theme-promo': 'Promo Banner',
    'theme-faq': 'FAQ',
  };
  return titles[type] || type;
}

function renderContentFields(type, content, onChange, availableProducts = []) {
  switch (type) {
    case 'theme-promo':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Supporting copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={3} /></FieldGroup>
        <FieldGroup label="Button label"><input type="text" value={content.buttonText || ''} onChange={e => onChange('buttonText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Button link"><input type="text" value={content.buttonLink || ''} onChange={e => onChange('buttonLink', e.target.value)} placeholder="/shop" /></FieldGroup>
      </>;
    case 'theme-faq':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        {[1, 2, 3].map((number) => <React.Fragment key={number}><FieldGroup label={`Question ${number}`}><input type="text" value={content[`question${number}`] || ''} onChange={e => onChange(`question${number}`, e.target.value)} /></FieldGroup><FieldGroup label={`Answer ${number}`}><textarea value={content[`answer${number}`] || ''} onChange={e => onChange(`answer${number}`, e.target.value)} rows={2} /></FieldGroup></React.Fragment>)}
      </>;
    case 'gallery-hero':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Headline"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Supporting copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={3} /></FieldGroup>
        <FieldGroup label="Button text"><input type="text" value={content.buttonText || ''} onChange={e => onChange('buttonText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Button link"><input type="text" value={content.buttonLink || ''} onChange={e => onChange('buttonLink', e.target.value)} placeholder="/shop" /></FieldGroup>
        <ThemeImageField label="Hero image" value={content.image || ''} onChange={value => onChange('image', value)} />
        <ThemeImageField label="Mobile hero image (optional)" value={content.mobileImage || ''} onChange={value => onChange('mobileImage', value)} />
      </>;
    case 'gallery-collections': {
      const galleryCategories = [...new Set(availableProducts.map((product) => product.category).filter(Boolean))];
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        {galleryCategories.length > 0 && <ChoiceMultiSelect label="Collections to show" options={galleryCategories} value={content.categoryNames || []} onChange={value => onChange('categoryNames', value)} />}
      </>;
    }
    case 'gallery-featured-works':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/shop" /></FieldGroup>
        {availableProducts.length > 0 && <ProductMultiSelect label="Products to show" products={availableProducts} value={content.productIds || []} onChange={value => onChange('productIds', value)} />}
      </>;
    case 'gallery-statement':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Statement copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={4} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/about" /></FieldGroup>
        <ThemeImageField label="Image" value={content.image || ''} onChange={value => onChange('image', value)} />
      </>;
    case 'atelier-hero':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Headline"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Supporting copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={3} /></FieldGroup>
        <FieldGroup label="Button text"><input type="text" value={content.buttonText || ''} onChange={e => onChange('buttonText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Button link"><input type="text" value={content.buttonLink || ''} onChange={e => onChange('buttonLink', e.target.value)} placeholder="/shop" /></FieldGroup>
        <ThemeImageField label="Hero image" value={content.image || ''} onChange={value => onChange('image', value)} />
        <ThemeImageField label="Mobile hero image (optional)" value={content.mobileImage || ''} onChange={value => onChange('mobileImage', value)} />
      </>;
    case 'atelier-category-mosaic': {
      const atelierCategories = [...new Set(availableProducts.map((product) => product.category).filter(Boolean))];
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        {atelierCategories.length > 0 && <ChoiceMultiSelect label="Rituals to show" options={atelierCategories} value={content.categoryNames || []} onChange={value => onChange('categoryNames', value)} />}
      </>;
    }
    case 'atelier-services':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Supporting copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={3} /></FieldGroup>
        <FieldGroup label="Button text"><input type="text" value={content.buttonText || ''} onChange={e => onChange('buttonText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Button link"><input type="text" value={content.buttonLink || ''} onChange={e => onChange('buttonLink', e.target.value)} placeholder="/shop" /></FieldGroup>
      </>;
    case 'atelier-bestsellers':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/shop" /></FieldGroup>
        {availableProducts.length > 0 && <ProductMultiSelect label="Products to show" products={availableProducts} value={content.productIds || []} onChange={value => onChange('productIds', value)} />}
      </>;
    case 'atelier-story':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Story copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={4} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/about" /></FieldGroup>
        <ThemeImageField label="Image" value={content.image || ''} onChange={value => onChange('image', value)} />
      </>;
    case 'edition-hero':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} placeholder="New collection" /></FieldGroup>
        <FieldGroup label="Headline"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} placeholder="Your campaign headline" /></FieldGroup>
        <FieldGroup label="Supporting copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={3} /></FieldGroup>
        <FieldGroup label="Button text"><input type="text" value={content.buttonText || ''} onChange={e => onChange('buttonText', e.target.value)} placeholder="Shop the collection" /></FieldGroup>
        <FieldGroup label="Button link"><input type="text" value={content.buttonLink || ''} onChange={e => onChange('buttonLink', e.target.value)} placeholder="/shop" /></FieldGroup>
        <ThemeImageField label="Hero image" value={content.image || ''} onChange={value => onChange('image', value)} />
        <ThemeImageField label="Mobile hero image (optional)" value={content.mobileImage || ''} onChange={value => onChange('mobileImage', value)} />
      </>;
    case 'edition-category-mosaic':
      const categories = [...new Set(availableProducts.map((product) => product.category).filter(Boolean))];
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        {categories.length > 0 && <><ChoiceMultiSelect label="Collections to show" options={categories} value={content.categoryNames || []} onChange={value => onChange('categoryNames', value)} /><small className="tb-settings-hint">Leave empty to use the first available collections.</small></>}
      </>;
    case 'edition-product-edit':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/shop" /></FieldGroup>
        {availableProducts.length > 0 && <><ProductMultiSelect label="Products to show" products={availableProducts} value={content.productIds || []} onChange={value => onChange('productIds', value)} /><small className="tb-settings-hint">Leave empty to use featured products automatically.</small></>}
      </>;
    case 'edition-story':
      return <>
        <FieldGroup label="Eyebrow"><input type="text" value={content.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} /></FieldGroup>
        <FieldGroup label="Heading"><input type="text" value={content.title || ''} onChange={e => onChange('title', e.target.value)} placeholder="Leave empty to use your store name" /></FieldGroup>
        <FieldGroup label="Story copy"><textarea value={content.body || ''} onChange={e => onChange('body', e.target.value)} rows={4} /></FieldGroup>
        <FieldGroup label="Link label"><input type="text" value={content.linkText || ''} onChange={e => onChange('linkText', e.target.value)} /></FieldGroup>
        <FieldGroup label="Link"><input type="text" value={content.link || ''} onChange={e => onChange('link', e.target.value)} placeholder="/about" /></FieldGroup>
      </>;
    case 'hero-banner':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Enter headline"
            />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={e => onChange('subtitle', e.target.value)}
              placeholder="Enter subtitle"
            />
          </FieldGroup>
          <FieldGroup label="Button Text">
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={e => onChange('buttonText', e.target.value)}
              placeholder="Shop Now"
            />
          </FieldGroup>
          <FieldGroup label="Button Link">
            <input
              type="text"
              value={content.buttonLink || ''}
              onChange={e => onChange('buttonLink', e.target.value)}
              placeholder="/shop"
            />
          </FieldGroup>
          <ThemeImageField label="Background image" value={content.backgroundImage || ''} onChange={value => onChange('backgroundImage', value)} />
        </>
      );

    case 'hero-slider':
      return (
        <>
          <p className="tb-settings-hint">Configure slides for your hero carousel</p>
          <SlideEditor
            slides={content.slides || []}
            onChange={(slides) => onChange('slides', slides)}
          />
        </>
      );

    case 'featured-products':
    case 'all-products':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder={type === 'all-products' ? 'All Products' : 'Featured Products'}
            />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={e => onChange('subtitle', e.target.value)}
              placeholder={type === 'all-products' ? 'Browse everything in store' : 'Our best sellers'}
            />
          </FieldGroup>
        </>
      );

    case 'categories-grid':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Shop by Category"
            />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={e => onChange('subtitle', e.target.value)}
              placeholder=""
            />
          </FieldGroup>
        </>
      );

    case 'testimonials':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="What Customers Say"
            />
          </FieldGroup>
        </>
      );

    case 'newsletter':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Subscribe to our Newsletter"
            />
          </FieldGroup>
          <FieldGroup label="Description">
            <textarea
              value={content.description || ''}
              onChange={e => onChange('description', e.target.value)}
              placeholder="Get updates on new products..."
              rows={3}
            />
          </FieldGroup>
          <FieldGroup label="Button Text">
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={e => onChange('buttonText', e.target.value)}
              placeholder="Subscribe"
            />
          </FieldGroup>
        </>
      );

    case 'text-image':
      return (
        <>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Our Story"
            />
          </FieldGroup>
          <FieldGroup label="Text">
            <textarea
              value={content.text || ''}
              onChange={e => onChange('text', e.target.value)}
              placeholder="Tell your brand story..."
              rows={4}
            />
          </FieldGroup>
          <FieldGroup label="Button Text">
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={e => onChange('buttonText', e.target.value)}
              placeholder="Learn More"
            />
          </FieldGroup>
          <FieldGroup label="Button Link">
            <input
              type="text"
              value={content.buttonLink || ''}
              onChange={e => onChange('buttonLink', e.target.value)}
              placeholder="/about"
            />
          </FieldGroup>
          <ThemeImageField label="Image" value={content.image || ''} onChange={value => onChange('image', value)} />
        </>
      );

    case 'announcement-bar':
      return (
        <>
          <FieldGroup label="Text">
            <input
              type="text"
              value={content.text || ''}
              onChange={e => onChange('text', e.target.value)}
              placeholder="Free shipping on orders over $50!"
            />
          </FieldGroup>
          <FieldGroup label="Link (optional)">
            <input
              type="text"
              value={content.link || ''}
              onChange={e => onChange('link', e.target.value)}
              placeholder="/sale"
            />
          </FieldGroup>
        </>
      );

    case 'video-banner':
      return (
        <>
          <FieldGroup label="Video URL">
            <input
              type="text"
              value={content.videoUrl || ''}
              onChange={e => onChange('videoUrl', e.target.value)}
              placeholder="YouTube, Vimeo, or direct video URL"
            />
            <span className="tb-field-hint">Supports YouTube, Vimeo, or MP4/WebM links</span>
          </FieldGroup>
          <FieldGroup label="Poster Image (fallback)">
            <input
              type="text"
              value={content.posterImage || ''}
              onChange={e => onChange('posterImage', e.target.value)}
              placeholder="https://..."
            />
          </FieldGroup>
          <FieldGroup label="Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Welcome"
            />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={e => onChange('subtitle', e.target.value)}
              placeholder="Explore our collection"
            />
          </FieldGroup>
          <FieldGroup label="Button Text">
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={e => onChange('buttonText', e.target.value)}
              placeholder="Shop Now"
            />
          </FieldGroup>
          <FieldGroup label="Button Link">
            <input
              type="text"
              value={content.buttonLink || ''}
              onChange={e => onChange('buttonLink', e.target.value)}
              placeholder="/shop"
            />
          </FieldGroup>
        </>
      );

    case 'trust-badges':
      return (
        <>
          <FieldGroup label="Section Title">
            <input
              type="text"
              value={content.title || ''}
              onChange={e => onChange('title', e.target.value)}
              placeholder="Why Shop With Us"
            />
          </FieldGroup>
          <p className="tb-settings-hint">Configure trust badges for your store</p>
          <BadgeEditor
            badges={content.badges || []}
            onChange={(badges) => onChange('badges', badges)}
          />
        </>
      );

    case 'ad-placement':
      return (
        <p className="tb-settings-hint">
          Ads are loaded dynamically from your active ad campaigns.
          Configure ad placements and settings below.
        </p>
      );

    default:
      return <p className="tb-settings-empty">No content settings available</p>;
  }
}

function renderLayoutFields(type, settings, onChange) {
  switch (type) {
    case 'theme-promo':
    case 'theme-faq':
      return <p className="tb-settings-hint">This universal module adapts to each active theme’s typography, spacing, and mobile layout automatically.</p>;
    case 'gallery-collections':
      return <><FieldGroup label="Collections to show"><select value={settings.maxCategories || 3} onChange={e => onChange('maxCategories', parseInt(e.target.value, 10))}><option value={2}>2 collections</option><option value={3}>3 collections</option><option value={4}>4 collections</option><option value={5}>5 collections</option><option value={6}>6 collections</option></select></FieldGroup><SectionSpacingControl settings={settings} onChange={onChange} /></>;
    case 'gallery-featured-works':
      return <><FieldGroup label="Works to show"><select value={settings.count || 6} onChange={e => onChange('count', parseInt(e.target.value, 10))}><option value={3}>3 works</option><option value={4}>4 works</option><option value={6}>6 works</option><option value={8}>8 works</option><option value={12}>12 works</option></select></FieldGroup><SectionSpacingControl settings={settings} onChange={onChange} /></>;
    case 'gallery-hero':
    case 'atelier-hero':
    case 'edition-hero':
      return <>
        <FieldGroup label="Hero height"><select value={settings.minHeight || (type === 'atelier-hero' ? '680px' : '760px')} onChange={e => onChange('minHeight', e.target.value)}><option value="560px">Compact</option><option value="680px">Standard</option><option value="760px">Tall</option><option value="860px">Extra tall</option><option value="100svh">Full screen</option></select></FieldGroup>
        <FieldGroup label="Content position"><select value={settings.contentPosition || 'left'} onChange={e => onChange('contentPosition', e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></FieldGroup>
        <FieldGroup label="Image focal point"><select value={settings.imagePosition || 'center'} onChange={e => onChange('imagePosition', e.target.value)}><option value="center">Center</option><option value="top">Top</option><option value="bottom">Bottom</option><option value="left">Left</option><option value="right">Right</option></select></FieldGroup>
      </>;
    case 'gallery-statement':
      return <><SectionSpacingControl settings={settings} onChange={onChange} /><p className="tb-settings-hint">This editorial composition is responsive by design. Use the device preview above to check its natural layout at each breakpoint.</p></>;
    case 'edition-category-mosaic':
      return <><FieldGroup label="Categories to show"><select value={settings.maxCategories || 3} onChange={e => onChange('maxCategories', parseInt(e.target.value, 10))}><option value={2}>2 categories</option><option value={3}>3 categories</option><option value={4}>4 categories</option><option value={5}>5 categories</option><option value={6}>6 categories</option></select></FieldGroup><SectionSpacingControl settings={settings} onChange={onChange} /></>;
    case 'edition-product-edit':
      return <><FieldGroup label="Products to show"><select value={settings.count || 8} onChange={e => onChange('count', parseInt(e.target.value, 10))}><option value={4}>4 products</option><option value={6}>6 products</option><option value={8}>8 products</option><option value={12}>12 products</option><option value={16}>16 products</option></select></FieldGroup><SectionSpacingControl settings={settings} onChange={onChange} /></>;
    case 'edition-story':
      return <><SectionSpacingControl settings={settings} onChange={onChange} /><p className="tb-settings-hint">Edition keeps this composition intentionally fixed so it remains polished on mobile.</p></>;
    case 'atelier-category-mosaic':
    case 'atelier-bestsellers':
    case 'atelier-services':
    case 'atelier-story':
      return <SectionSpacingControl settings={settings} onChange={onChange} />;
    case 'hero-banner':
    case 'hero-slider':
    case 'video-banner':
      return (
        <>
          <FieldGroup label="Height">
            <select
              value={settings.height || '500px'}
              onChange={e => onChange('height', e.target.value)}
            >
              <option value="400px">Small (400px)</option>
              <option value="500px">Medium (500px)</option>
              <option value="600px">Large (600px)</option>
              <option value="700px">Extra Large (700px)</option>
              <option value="100vh">Full Screen</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Text Alignment">
            <select
              value={settings.textAlign || 'center'}
              onChange={e => onChange('textAlign', e.target.value)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </FieldGroup>
          {type === 'video-banner' && (
            <>
              <FieldGroup label="Autoplay">
                <ToggleSwitch
                  checked={settings.autoplay !== false}
                  onChange={checked => onChange('autoplay', checked)}
                />
              </FieldGroup>
              <FieldGroup label="Loop">
                <ToggleSwitch
                  checked={settings.loop !== false}
                  onChange={checked => onChange('loop', checked)}
                />
              </FieldGroup>
              <FieldGroup label="Show Controls">
                <ToggleSwitch
                  checked={settings.showControls === true}
                  onChange={checked => onChange('showControls', checked)}
                />
              </FieldGroup>
              <FieldGroup label="Video Fit">
                <select
                  value={settings.objectFit || 'cover'}
                  onChange={e => onChange('objectFit', e.target.value)}
                >
                  <option value="cover">Cover (fill area)</option>
                  <option value="contain">Contain (show all)</option>
                </select>
              </FieldGroup>
            </>
          )}
        </>
      );

    case 'featured-products':
      return (
        <>
          <FieldGroup label="Number of Products">
            <select
              value={settings.count || 8}
              onChange={e => onChange('count', parseInt(e.target.value))}
            >
              <option value={4}>4 Products</option>
              <option value={6}>6 Products</option>
              <option value={8}>8 Products</option>
              <option value={12}>12 Products</option>
              <option value={16}>16 Products</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Columns">
            <select
              value={settings.columns || 4}
              onChange={e => onChange('columns', parseInt(e.target.value))}
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
              <option value={5}>5 Columns</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Show Price">
            <ToggleSwitch
              checked={settings.showPrice !== false}
              onChange={checked => onChange('showPrice', checked)}
            />
          </FieldGroup>
          <FieldGroup label="Show Quick Add">
            <ToggleSwitch
              checked={settings.showQuickAdd === true}
              onChange={checked => onChange('showQuickAdd', checked)}
            />
          </FieldGroup>
        </>
      );

    case 'all-products':
      return (
        <>
          <FieldGroup label="Products per page">
            <select
              value={settings.productsPerPage || 12}
              onChange={e => onChange('productsPerPage', parseInt(e.target.value))}
            >
              <option value={8}>8 Products</option>
              <option value={12}>12 Products</option>
              <option value={16}>16 Products</option>
              <option value={24}>24 Products</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Default View">
            <select
              value={settings.defaultView || 'grid'}
              onChange={e => onChange('defaultView', e.target.value)}
            >
              <option value="grid">Grid</option>
              <option value="list">List</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Show Search & Filters">
            <ToggleSwitch
              checked={settings.showControls !== false}
              onChange={checked => onChange('showControls', checked)}
            />
          </FieldGroup>
        </>
      );

    case 'categories-grid':
      return (
        <>
          <FieldGroup label="Columns">
            <select
              value={settings.columns || 4}
              onChange={e => onChange('columns', parseInt(e.target.value))}
            >
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
              <option value={5}>5 Columns</option>
              <option value={6}>6 Columns</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Image Style">
            <select
              value={settings.imageStyle || 'square'}
              onChange={e => onChange('imageStyle', e.target.value)}
            >
              <option value="square">Square</option>
              <option value="circle">Circle</option>
              <option value="portrait">Portrait</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Show Product Count">
            <ToggleSwitch
              checked={settings.showCount !== false}
              onChange={checked => onChange('showCount', checked)}
            />
          </FieldGroup>
        </>
      );

    case 'testimonials':
      return (
        <>
          <FieldGroup label="Layout">
            <select
              value={settings.layout || 'carousel'}
              onChange={e => onChange('layout', e.target.value)}
            >
              <option value="carousel">Carousel</option>
              <option value="grid">Grid</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Max Items">
            <select
              value={settings.maxItems || 6}
              onChange={e => onChange('maxItems', parseInt(e.target.value))}
            >
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={9}>9</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Show Photos">
            <ToggleSwitch
              checked={settings.showPhotos !== false}
              onChange={checked => onChange('showPhotos', checked)}
            />
          </FieldGroup>
        </>
      );

    case 'text-image':
      return (
        <>
          <FieldGroup label="Image Position">
            <select
              value={settings.imagePosition || 'right'}
              onChange={e => onChange('imagePosition', e.target.value)}
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'newsletter':
      return (
        <>
          <FieldGroup label="Layout">
            <select
              value={settings.layout || 'horizontal'}
              onChange={e => onChange('layout', e.target.value)}
            >
              <option value="horizontal">Horizontal</option>
              <option value="stacked">Stacked</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'trust-badges':
      return (
        <>
          <FieldGroup label="Layout">
            <select
              value={settings.layout || 'horizontal'}
              onChange={e => onChange('layout', e.target.value)}
            >
              <option value="horizontal">Horizontal Row</option>
              <option value="grid">Grid</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'ad-placement':
      return (
        <>
          <FieldGroup label="Ad Placement">
            <select
              value={settings.placement || 'HOMEPAGE_BANNER'}
              onChange={e => onChange('placement', e.target.value)}
            >
              <option value="HOMEPAGE_HERO">Homepage Hero</option>
              <option value="HOMEPAGE_BANNER">Homepage Banner</option>
              <option value="SHOP_INLINE">Shop Page Inline</option>
              <option value="SHOP_SIDEBAR">Shop Page Sidebar</option>
              <option value="CATEGORY_INLINE">Category Inline</option>
              <option value="ABOUT_BANNER">About Page Banner</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Max Ads">
            <select
              value={settings.maxAds || 1}
              onChange={e => onChange('maxAds', parseInt(e.target.value))}
            >
              <option value={1}>1 Ad</option>
              <option value={2}>2 Ads</option>
              <option value={3}>3 Ads</option>
            </select>
          </FieldGroup>
        </>
      );

    default:
      return <p className="tb-settings-empty">No layout settings available</p>;
  }
}

function renderStyleFields(type, settings, onChange) {
  // Helper to create color picker props
  const colorPickerProps = (label, value, defaultValue, settingKey) => ({
    label,
    value: value || defaultValue,
    defaultValue,
    onChange: (newValue) => onChange(settingKey, newValue),
  });

  switch (type) {
    case 'theme-promo':
      return <>
        <ColorPicker {...colorPickerProps('Surface', settings.backgroundColor, '#323232', 'backgroundColor')} />
        <ColorPicker {...colorPickerProps('Heading color', settings.textColor, '#f5f2eb', 'textColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.supportingTextColor, '#f5f2eb', 'supportingTextColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.buttonBackgroundColor, '#151513', 'buttonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.buttonTextColor, '#ffffff', 'buttonTextColor')} />
        <p className="tb-settings-hint">These colors control this campaign block directly and are retained in both storefront appearances.</p>
      </>;
    case 'theme-faq':
      return <div className="tb-palette-notice"><span className="tb-palette-notice__swatches"><i/><i/><i/></span><div><strong>Theme palette</strong><p>This widget inherits the store’s Light and Dark palettes for consistent contrast across every theme.</p></div></div>;
    case 'gallery-hero':
      return <>
        <ColorPicker {...colorPickerProps('Headline color', settings.titleColor, '#111111', 'titleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.bodyColor, '#111111', 'bodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.buttonBackgroundColor, '#111111', 'buttonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.buttonTextColor, '#ffffff', 'buttonTextColor')} />
        <p className="tb-settings-hint">Dark appearance</p>
        <ColorPicker {...colorPickerProps('Headline color', settings.darkTitleColor, '#111111', 'darkTitleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.darkBodyColor, '#111111', 'darkBodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.darkButtonBackgroundColor, '#111111', 'darkButtonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.darkButtonTextColor, '#ffffff', 'darkButtonTextColor')} />
      </>;
    case 'atelier-hero':
      return <>
        <ColorPicker {...colorPickerProps('Copy-panel surface', settings.surfaceColor, '#e9e3d7', 'surfaceColor')} />
        <ColorPicker {...colorPickerProps('Eyebrow color', settings.eyebrowColor, '#bd755f', 'eyebrowColor')} />
        <ColorPicker {...colorPickerProps('Headline color', settings.titleColor, '#2e3930', 'titleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.bodyColor, '#68736a', 'bodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.buttonBackgroundColor, '#2e3930', 'buttonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.buttonTextColor, '#fbfaf6', 'buttonTextColor')} />
        <p className="tb-settings-hint">Dark appearance</p>
        <ColorPicker {...colorPickerProps('Copy-panel surface', settings.darkSurfaceColor, '#343a32', 'darkSurfaceColor')} />
        <ColorPicker {...colorPickerProps('Eyebrow color', settings.darkEyebrowColor, '#db927b', 'darkEyebrowColor')} />
        <ColorPicker {...colorPickerProps('Headline color', settings.darkTitleColor, '#f6f1e8', 'darkTitleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.darkBodyColor, '#c8c0b4', 'darkBodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.darkButtonBackgroundColor, '#f6f1e8', 'darkButtonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.darkButtonTextColor, '#20231f', 'darkButtonTextColor')} />
      </>;
    case 'edition-hero':
      return <>
        <FieldGroup label="Image overlay"><input type="range" min="0" max="0.9" step="0.05" value={settings.overlayOpacity ?? 0.45} onChange={e => onChange('overlayOpacity', parseFloat(e.target.value))} /><span className="tb-range-value">{Math.round((settings.overlayOpacity ?? 0.45) * 100)}%</span></FieldGroup>
        <ColorPicker {...colorPickerProps('Hero surface', settings.surfaceColor, '#d9d7d1', 'surfaceColor')} />
        <ColorPicker {...colorPickerProps('Eyebrow color', settings.eyebrowColor, '#ffffff', 'eyebrowColor')} />
        <ColorPicker {...colorPickerProps('Overlay color', settings.overlayColor, '#000000', 'overlayColor')} />
        <ColorPicker {...colorPickerProps('Headline color', settings.titleColor, '#ffffff', 'titleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.bodyColor, '#ffffff', 'bodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.buttonBackgroundColor, '#ffffff', 'buttonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.buttonTextColor, '#151513', 'buttonTextColor')} />
        <p className="tb-settings-hint">Dark appearance</p>
        <ColorPicker {...colorPickerProps('Hero surface', settings.darkSurfaceColor, '#373630', 'darkSurfaceColor')} />
        <ColorPicker {...colorPickerProps('Eyebrow color', settings.darkEyebrowColor, '#f5f2eb', 'darkEyebrowColor')} />
        <ColorPicker {...colorPickerProps('Headline color', settings.darkTitleColor, '#f5f2eb', 'darkTitleColor')} />
        <ColorPicker {...colorPickerProps('Supporting copy color', settings.darkBodyColor, '#f5f2eb', 'darkBodyColor')} />
        <ColorPicker {...colorPickerProps('Button background', settings.darkButtonBackgroundColor, '#f5f2eb', 'darkButtonBackgroundColor')} />
        <ColorPicker {...colorPickerProps('Button label', settings.darkButtonTextColor, '#171716', 'darkButtonTextColor')} />
      </>;
      return <div className="tb-palette-notice"><span className="tb-palette-notice__swatches"><i/><i/><i/></span><div><strong>Theme palette</strong><p>Section colours inherit from the Light and Dark palettes in Global settings, so the full experience stays accessible in both modes.</p></div></div>;
    case 'edition-category-mosaic':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="categories" />;
    case 'edition-product-edit':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="products" />;
    case 'edition-story':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="story" />;
    case 'gallery-collections':
    case 'gallery-featured-works':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="products" />;
    case 'gallery-statement':
      return <GalleryStatementColors settings={settings} onChange={onChange} />;
    case 'atelier-category-mosaic':
    case 'atelier-bestsellers':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="products" />;
    case 'atelier-services':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="story" />;
    case 'atelier-story':
      return <EditionSectionColors settings={settings} onChange={onChange} kind="story" />;
    case 'hero-banner':
    case 'hero-slider':
    case 'video-banner':
      return (
        <>
          <FieldGroup label="Overlay Opacity">
            <input
              type="range"
              min="0"
              max="0.9"
              step="0.1"
              value={settings.overlayOpacity || 0.3}
              onChange={e => onChange('overlayOpacity', parseFloat(e.target.value))}
            />
            <span className="tb-range-value">{Math.round((settings.overlayOpacity || 0.3) * 100)}%</span>
          </FieldGroup>
          <ColorPicker {...colorPickerProps("Overlay Color", settings.overlayColor, "#000000", "overlayColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#ffffff", "titleColor")} />
          <ColorPicker {...colorPickerProps("Subtitle Color", settings.subtitleColor, "#e5e5e5", "subtitleColor")} />
          <ColorPicker {...colorPickerProps("Button Background", settings.buttonBgColor, "#2563eb", "buttonBgColor")} />
          <ColorPicker {...colorPickerProps("Button Text", settings.buttonTextColor, "#ffffff", "buttonTextColor")} />
          {(type === 'hero-banner' || type === 'hero-slider') && <><p className="tb-settings-hint">Dark appearance</p><ColorPicker {...colorPickerProps("Title Color", settings.darkTitleColor, "#f3f4f6", "darkTitleColor")} /><ColorPicker {...colorPickerProps("Subtitle Color", settings.darkSubtitleColor, "#9ca3af", "darkSubtitleColor")} /><ColorPicker {...colorPickerProps("Button Background", settings.darkButtonBgColor, "#f3f4f6", "darkButtonBgColor")} /><ColorPicker {...colorPickerProps("Button Text", settings.darkButtonTextColor, "#0b0f17", "darkButtonTextColor")} /></>}
          {(type === 'hero-banner' || type === 'hero-slider') && <><ColorPicker {...colorPickerProps("Overlay Color", settings.darkOverlayColor, "#000000", "darkOverlayColor")} /><FieldGroup label="Dark overlay opacity"><input type="range" min="0" max="0.9" step="0.1" value={settings.darkOverlayOpacity ?? settings.overlayOpacity ?? 0.3} onChange={e => onChange('darkOverlayOpacity', parseFloat(e.target.value))} /></FieldGroup></>}
          <FieldGroup label="Button Style">
            <select
              value={settings.buttonStyle || 'filled'}
              onChange={e => onChange('buttonStyle', e.target.value)}
            >
              <option value="filled">Filled</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Button Border Radius">
            <select
              value={settings.buttonRadius || '8px'}
              onChange={e => onChange('buttonRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="4px">Slightly Rounded</option>
              <option value="8px">Rounded</option>
              <option value="20px">Pill</option>
              <option value="50px">Full Round</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'featured-products':
    case 'all-products':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#ffffff", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#111827", "titleColor")} />
          {type === 'all-products' ? (
            <>
              <ColorPicker {...colorPickerProps("Text Color", settings.textColor, "#111827", "textColor")} />
              <ColorPicker {...colorPickerProps("Muted Text", settings.mutedColor, "#6b7280", "mutedColor")} />
              <ColorPicker {...colorPickerProps("Accent Color", settings.accentColor, "#0d7463", "accentColor")} />
              <ColorPicker {...colorPickerProps("Surface Color", settings.surfaceColor, "#f9fafb", "surfaceColor")} />
              <ColorPicker {...colorPickerProps("Border Color", settings.borderColor, "#e5e7eb", "borderColor")} />
            </>
          ) : (
            <>
              <ColorPicker {...colorPickerProps("Subtitle Color", settings.subtitleColor, "#6b7280", "subtitleColor")} />
              <ColorPicker {...colorPickerProps("Product Name Color", settings.productNameColor, "#111827", "productNameColor")} />
              <ColorPicker {...colorPickerProps("Price Color", settings.priceColor, "#2563eb", "priceColor")} />
              <ColorPicker {...colorPickerProps("Card Background", settings.cardBgColor, "#ffffff", "cardBgColor")} />
              <ColorPicker {...colorPickerProps("Card Border Color", settings.cardBorderColor, "#e5e7eb", "cardBorderColor")} />
              <FieldGroup label="Card Border Radius">
                <select
                  value={settings.cardRadius || '12px'}
                  onChange={e => onChange('cardRadius', e.target.value)}
                >
                  <option value="0px">Square</option>
                  <option value="8px">Slightly Rounded</option>
                  <option value="12px">Rounded</option>
                  <option value="16px">More Rounded</option>
                  <option value="24px">Very Rounded</option>
                </select>
              </FieldGroup>
              <FieldGroup label="Card Shadow">
                <select
                  value={settings.cardShadow || 'medium'}
                  onChange={e => onChange('cardShadow', e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </FieldGroup>
              <ColorPicker {...colorPickerProps("Quick Add Button", settings.quickAddColor, "#2563eb", "quickAddColor")} />
              <p className="tb-settings-hint">Dark appearance</p><ColorPicker {...colorPickerProps("Section background", settings.darkBackgroundColor, "#111827", "darkBackgroundColor")} /><ColorPicker {...colorPickerProps("Heading", settings.darkTitleColor, "#f3f4f6", "darkTitleColor")} /><ColorPicker {...colorPickerProps("Product name", settings.darkProductNameColor, "#f3f4f6", "darkProductNameColor")} /><ColorPicker {...colorPickerProps("Price", settings.darkPriceColor, "#93c5fd", "darkPriceColor")} /><ColorPicker {...colorPickerProps("Card background", settings.darkCardBgColor, "#1f2937", "darkCardBgColor")} /><ColorPicker {...colorPickerProps("Quick add", settings.darkQuickAddColor, "#93c5fd", "darkQuickAddColor")} />
            </>
          )}
        </>
      );

    case 'categories-grid':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#ffffff", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#111827", "titleColor")} />
          <ColorPicker {...colorPickerProps("Category Name Color", settings.categoryNameColor, "#111827", "categoryNameColor")} />
          <p className="tb-settings-hint">Dark appearance</p><ColorPicker {...colorPickerProps("Section background", settings.darkBackgroundColor, "#111827", "darkBackgroundColor")} /><ColorPicker {...colorPickerProps("Heading", settings.darkTitleColor, "#f3f4f6", "darkTitleColor")} /><ColorPicker {...colorPickerProps("Category name", settings.darkCategoryNameColor, "#f3f4f6", "darkCategoryNameColor")} /><ColorPicker {...colorPickerProps("Count", settings.darkCountColor, "#9ca3af", "darkCountColor")} />
          <ColorPicker {...colorPickerProps("Count Badge Color", settings.countColor, "#6b7280", "countColor")} />
          <ColorPicker {...colorPickerProps("Overlay Color", settings.overlayColor, "#000000", "overlayColor")} />
          <FieldGroup label="Overlay Opacity">
            <input
              type="range"
              min="0"
              max="0.7"
              step="0.1"
              value={settings.overlayOpacity || 0.3}
              onChange={e => onChange('overlayOpacity', parseFloat(e.target.value))}
            />
            <span className="tb-range-value">{Math.round((settings.overlayOpacity || 0.3) * 100)}%</span>
          </FieldGroup>
          <FieldGroup label="Card Border Radius">
            <select
              value={settings.cardRadius || '12px'}
              onChange={e => onChange('cardRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="8px">Slightly Rounded</option>
              <option value="12px">Rounded</option>
              <option value="50%">Circle</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Hover Effect">
            <select
              value={settings.hoverEffect || 'zoom'}
              onChange={e => onChange('hoverEffect', e.target.value)}
            >
              <option value="none">None</option>
              <option value="zoom">Zoom</option>
              <option value="lift">Lift</option>
              <option value="glow">Glow</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'testimonials':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#f9fafb", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#111827", "titleColor")} />
          <ColorPicker {...colorPickerProps("Card Background", settings.cardBgColor, "#ffffff", "cardBgColor")} />
          <ColorPicker {...colorPickerProps("Quote Text Color", settings.quoteColor, "#374151", "quoteColor")} />
          <ColorPicker {...colorPickerProps("Author Name Color", settings.authorColor, "#111827", "authorColor")} />
          <ColorPicker {...colorPickerProps("Star Color", settings.starColor, "#fbbf24", "starColor")} />
          <FieldGroup label="Card Border Radius">
            <select
              value={settings.cardRadius || '12px'}
              onChange={e => onChange('cardRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="8px">Slightly Rounded</option>
              <option value="12px">Rounded</option>
              <option value="20px">Very Rounded</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'newsletter':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#f9fafb", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#111827", "titleColor")} />
          <ColorPicker {...colorPickerProps("Description Color", settings.descriptionColor, "#6b7280", "descriptionColor")} />
          <ColorPicker {...colorPickerProps("Input Background", settings.inputBgColor, "#ffffff", "inputBgColor")} />
          <ColorPicker {...colorPickerProps("Input Border Color", settings.inputBorderColor, "#d1d5db", "inputBorderColor")} />
          <ColorPicker {...colorPickerProps("Input Text Color", settings.inputTextColor, "#111827", "inputTextColor")} />
          <ColorPicker {...colorPickerProps("Button Background", settings.buttonBgColor, "#2563eb", "buttonBgColor")} />
          <ColorPicker {...colorPickerProps("Button Text Color", settings.buttonTextColor, "#ffffff", "buttonTextColor")} />
          <FieldGroup label="Button Border Radius">
            <select
              value={settings.buttonRadius || '8px'}
              onChange={e => onChange('buttonRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="4px">Slightly Rounded</option>
              <option value="8px">Rounded</option>
              <option value="20px">Pill</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Input Border Radius">
            <select
              value={settings.inputRadius || '8px'}
              onChange={e => onChange('inputRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="4px">Slightly Rounded</option>
              <option value="8px">Rounded</option>
              <option value="20px">Pill</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'text-image':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#ffffff", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Title Color", settings.titleColor, "#111827", "titleColor")} />
          <ColorPicker {...colorPickerProps("Text Color", settings.textColor, "#4b5563", "textColor")} />
          <ColorPicker {...colorPickerProps("Button Background", settings.buttonBgColor, "#2563eb", "buttonBgColor")} />
          <ColorPicker {...colorPickerProps("Button Text Color", settings.buttonTextColor, "#ffffff", "buttonTextColor")} />
          <FieldGroup label="Button Style">
            <select
              value={settings.buttonStyle || 'filled'}
              onChange={e => onChange('buttonStyle', e.target.value)}
            >
              <option value="filled">Filled</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Image Border Radius">
            <select
              value={settings.imageRadius || '12px'}
              onChange={e => onChange('imageRadius', e.target.value)}
            >
              <option value="0px">Square</option>
              <option value="8px">Slightly Rounded</option>
              <option value="12px">Rounded</option>
              <option value="24px">Very Rounded</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Content Padding">
            <select
              value={settings.contentPadding || 'medium'}
              onChange={e => onChange('contentPadding', e.target.value)}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'trust-badges':
      return (
        <>
          <ColorPicker {...colorPickerProps("Section Background", settings.backgroundColor, "#ffffff", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Icon Color", settings.iconColor, "#2563eb", "iconColor")} />
          <ColorPicker {...colorPickerProps("Label Color", settings.labelColor, "#111827", "labelColor")} />
          <ColorPicker {...colorPickerProps("Description Color", settings.descriptionColor, "#6b7280", "descriptionColor")} />
          <FieldGroup label="Icon Size">
            <select
              value={settings.iconSize || 'medium'}
              onChange={e => onChange('iconSize', e.target.value)}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Section Padding">
            <select
              value={settings.sectionPadding || 'medium'}
              onChange={e => onChange('sectionPadding', e.target.value)}
            >
              <option value="small">Compact</option>
              <option value="medium">Normal</option>
              <option value="large">Spacious</option>
            </select>
          </FieldGroup>
        </>
      );

    case 'announcement-bar':
      return (
        <>
          <ColorPicker {...colorPickerProps("Background Color", settings.backgroundColor, "#1a1a2e", "backgroundColor")} />
          <ColorPicker {...colorPickerProps("Text Color", settings.textColor, "#ffffff", "textColor")} />
          <ColorPicker {...colorPickerProps("Link Color", settings.linkColor, "#60a5fa", "linkColor")} />
          <ColorPicker {...colorPickerProps("Close Button Color", settings.closeColor, "#9ca3af", "closeColor")} />
          <FieldGroup label="Font Size">
            <select
              value={settings.fontSize || 'small'}
              onChange={e => onChange('fontSize', e.target.value)}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Padding">
            <select
              value={settings.padding || 'medium'}
              onChange={e => onChange('padding', e.target.value)}
            >
              <option value="small">Compact</option>
              <option value="medium">Normal</option>
              <option value="large">Spacious</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Dismissible">
            <ToggleSwitch
              checked={settings.dismissible !== false}
              onChange={checked => onChange('dismissible', checked)}
            />
          </FieldGroup>
        </>
      );

    case 'ad-placement':
      return (
        <>
          <ColorPicker {...colorPickerProps("Background Color", settings.backgroundColor, "transparent", "backgroundColor")} />
          <FieldGroup label="Section Padding">
            <select
              value={settings.padding || '20px 0'}
              onChange={e => onChange('padding', e.target.value)}
            >
              <option value="0">None</option>
              <option value="10px 0">Small</option>
              <option value="20px 0">Medium</option>
              <option value="40px 0">Large</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Show 'Advertise' Link">
            <ToggleSwitch
              checked={settings.showAdvertiseBlurb !== false}
              onChange={checked => onChange('showAdvertiseBlurb', checked)}
            />
            <span className="tb-field-hint">Shows "Advertise with us" when no ads are running</span>
          </FieldGroup>
        </>
      );

    default:
      return <p className="tb-settings-empty">No style settings available</p>;
  }
}

// Field Group Component
function SectionSpacingControl({ settings, onChange }) {
  return <FieldGroup label="Section spacing"><select value={settings.spacing || 'standard'} onChange={e => onChange('spacing', e.target.value)}><option value="compact">Compact</option><option value="standard">Standard</option><option value="spacious">Spacious</option></select></FieldGroup>;
}

function EditionSectionColors({ settings, onChange, kind }) {
  const isStory = kind === 'story';
  const copyKey = kind === 'categories' ? 'labelColor' : (isStory ? 'copyColor' : 'copyColor');
  const darkCopyKey = kind === 'categories' ? 'darkLabelColor' : (isStory ? 'darkCopyColor' : 'darkCopyColor');
  const copyLabel = kind === 'categories' ? 'Image label' : (isStory ? 'Supporting copy' : 'Product and link text');
  const defaults = isStory
    ? { surface: '#151513', title: '#ffffff', copy: '#d9d7d1' }
    : { surface: '#ffffff', title: '#151513', copy: kind === 'categories' ? '#ffffff' : '#151513' };
  const darkDefaults = isStory
    ? { surface: '#080807', title: '#f5f2eb', copy: '#b8b4aa' }
    : { surface: '#171716', title: '#f5f2eb', copy: kind === 'categories' ? '#ffffff' : '#f5f2eb' };
  return <>
    <ColorPicker label="Surface" value={settings.backgroundColor || defaults.surface} defaultValue={defaults.surface} onChange={value => onChange('backgroundColor', value)} />
    <ColorPicker label="Heading" value={settings.titleColor || defaults.title} defaultValue={defaults.title} onChange={value => onChange('titleColor', value)} />
    <ColorPicker label={copyLabel} value={settings[copyKey] || defaults.copy} defaultValue={defaults.copy} onChange={value => onChange(copyKey, value)} />
    {isStory && <ColorPicker label="Link" value={settings.linkColor || defaults.title} defaultValue={defaults.title} onChange={value => onChange('linkColor', value)} />}
    <p className="tb-settings-hint">Dark appearance</p>
    <ColorPicker label="Surface" value={settings.darkBackgroundColor || darkDefaults.surface} defaultValue={darkDefaults.surface} onChange={value => onChange('darkBackgroundColor', value)} />
    <ColorPicker label="Heading" value={settings.darkTitleColor || darkDefaults.title} defaultValue={darkDefaults.title} onChange={value => onChange('darkTitleColor', value)} />
    <ColorPicker label={copyLabel} value={settings[darkCopyKey] || darkDefaults.copy} defaultValue={darkDefaults.copy} onChange={value => onChange(darkCopyKey, value)} />
    {isStory && <ColorPicker label="Link" value={settings.darkLinkColor || darkDefaults.title} defaultValue={darkDefaults.title} onChange={value => onChange('darkLinkColor', value)} />}
  </>;
}

function GalleryStatementColors({ settings, onChange }) {
  const field = (label, key, fallback) => <ColorPicker label={label} value={settings[key] || fallback} defaultValue={fallback} onChange={value => onChange(key, value)} />;
  return <>{field('Panel surface', 'backgroundColor', '#d9ff36')}{field('Heading', 'titleColor', '#111111')}{field('Supporting copy', 'copyColor', '#111111')}{field('Button background', 'linkColor', '#111111')}{field('Button label', 'buttonTextColor', '#ffffff')}<p className="tb-settings-hint">Dark appearance</p>{field('Panel surface', 'darkBackgroundColor', '#cfff22')}{field('Heading', 'darkTitleColor', '#0a0a0a')}{field('Supporting copy', 'darkCopyColor', '#0a0a0a')}{field('Button background', 'darkLinkColor', '#0a0a0a')}{field('Button label', 'darkButtonTextColor', '#ffffff')}</>;
}

function FieldGroup({ label, children }) {
  return (
    <div className="tb-field-group">
      <label className="tb-field-group__label">{label}</label>
      <div className="tb-field-group__control">{children}</div>
    </div>
  );
}

// Theme imagery is deliberately backed by the merchant media library rather
// than a theme-only upload path. That keeps an uploaded hero/story image
// immediately available in Media and reusable by product editing too.
function ThemeImageField({ label, value = '', onChange }) {
  const fileInputRef = useRef(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file (PNG, JPG, WebP, GIF, or similar).');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data?.url || '');
    } catch (err) {
      setError(err?.response?.data?.error || 'Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return <FieldGroup label={label}>
    <div className="tb-media-field">
      {value ? <img className="tb-media-field__preview" src={value} alt="Selected" /> : <div className="tb-media-field__empty" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
      </div>}
      <div className="tb-media-field__actions">
        <button type="button" onClick={() => setLibraryOpen(true)}>Choose from library</button>
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload image'}</button>
        {value && <button type="button" className="tb-media-field__remove" onClick={() => onChange('')}>Remove</button>}
      </div>
      <input ref={fileInputRef} className="tb-media-field__file-input" type="file" accept="image/*" onChange={uploadFile} />
      <button type="button" className="tb-media-field__url-toggle" onClick={() => setShowUrlInput((open) => !open)}>{showUrlInput ? 'Hide image URL' : 'Use image URL instead'}</button>
      {showUrlInput && <input type="url" value={value} onChange={(event) => onChange(event.target.value)} placeholder="https://..." aria-label={`${label} URL`} />}
      {error && <p className="tb-media-field__error">{error}</p>}
    </div>
    <ThemeMediaLibrary isOpen={libraryOpen} onClose={() => setLibraryOpen(false)} onSelect={(url) => { onChange(url); setLibraryOpen(false); }} />
  </FieldGroup>;
}

function ThemeMediaLibrary({ isOpen, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get('/media', { params: { type: 'image', page, limit: 24, ...(search.trim() ? { search: search.trim() } : {}) } });
        if (cancelled) return;
        setItems(Array.isArray(data?.media) ? data.media : []);
        setPages(data?.pagination?.pages || 1);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load your media library.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, page, search]);

  return <Modal isOpen={isOpen} onClose={onClose} title="Choose an image" size="xl">
    <div className="tb-media-library">
      <input className="tb-media-library__search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search your media library" />
      {loading ? <div className="tb-media-library__state">Loading images…</div> : error ? <div className="tb-media-library__state tb-media-library__state--error">{error}</div> : items.length ? <div className="tb-media-library__grid">
        {items.map((item) => <button type="button" key={item.id} className="tb-media-library__item" onClick={() => onSelect(item.url)} title={item.alt || item.originalName || 'Select image'}>
          <img src={item.url} alt={item.alt || item.originalName || ''} />
          <span>{item.alt || item.originalName || 'Untitled image'}</span>
        </button>)}
      </div> : <div className="tb-media-library__state">No images yet. Upload one from this section and it will appear here.</div>}
      {pages > 1 && <div className="tb-media-library__pagination"><button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button><span>Page {page} of {pages}</span><button type="button" disabled={page === pages} onClick={() => setPage((current) => current + 1)}>Next</button></div>}
    </div>
  </Modal>;
}

// Native multi-selects hide their state behind modifier keys. These compact
// pickers make selection obvious on touch, trackpads, and keyboards alike.
function ChoiceMultiSelect({ label, options, value = [], onChange }) {
  const selected = new Set(value.map(String));
  const toggle = (option) => {
    const optionValue = String(option);
    onChange(selected.has(optionValue)
      ? value.filter((item) => String(item) !== optionValue)
      : [...value, option]);
  };
  return <FieldGroup label={label}>
    <div className="tb-choice-select" role="group" aria-label={label}>
      <div className="tb-choice-select__summary">{value.length ? `${value.length} selected` : 'Automatic selection'}</div>
      <div className="tb-choice-select__options">
        {options.map((option) => <button type="button" key={String(option)} className={`tb-choice-select__option ${selected.has(String(option)) ? 'is-selected' : ''}`} onClick={() => toggle(option)}>
          <span className="tb-choice-select__check">{selected.has(String(option)) ? '✓' : ''}</span>{option}
        </button>)}
      </div>
    </div>
  </FieldGroup>;
}

function ProductMultiSelect({ label, products, value, onChange }) {
  const options = products.map((product) => ({ value: String(product.id || product._id), label: product.name || 'Untitled product' }));
  const selected = new Set(value.map(String));
  const toggle = (option) => onChange(selected.has(option.value)
    ? value.filter((item) => String(item) !== option.value)
    : [...value, option.value]);
  return <FieldGroup label={label}>
    <div className="tb-choice-select tb-choice-select--products" role="group" aria-label={label}>
      <div className="tb-choice-select__summary">{value.length ? `${value.length} selected` : 'Automatic selection'}</div>
      <div className="tb-choice-select__options">
        {options.map((option) => <button type="button" key={option.value} className={`tb-choice-select__option ${selected.has(option.value) ? 'is-selected' : ''}`} onClick={() => toggle(option)}>
          <span className="tb-choice-select__check">{selected.has(option.value) ? '✓' : ''}</span>{option.label}
        </button>)}
      </div>
    </div>
  </FieldGroup>;
}

// Color Picker Component - uses local state to avoid re-rendering issues
function ColorPicker({ label, value, defaultValue, onChange }) {
  const [localValue, setLocalValue] = useState(value);

  // Sync local value when prop value changes from parent
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    // Only propagate valid hex colors to parent
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleTextBlur = () => {
    // On blur, propagate the current value even if incomplete
    if (localValue && localValue !== value) {
      onChange(localValue);
    }
  };

  const handleColorChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <FieldGroup label={label}>
      <div className="tb-color-picker">
        <input
          type="color"
          value={localValue || defaultValue}
          onChange={handleColorChange}
        />
        <input
          type="text"
          value={localValue || defaultValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          placeholder={defaultValue}
        />
      </div>
    </FieldGroup>
  );
}

// Toggle Switch Component
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      className={`tb-toggle ${checked ? 'tb-toggle--active' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="tb-toggle__track">
        <span className="tb-toggle__thumb" />
      </span>
    </button>
  );
}

// Badge Editor Component for Trust Badges
function BadgeEditor({ badges = [], onChange }) {
  const iconOptions = [
    { value: 'shield', label: 'Shield (Secure)' },
    { value: 'truck', label: 'Truck (Shipping)' },
    { value: 'refresh', label: 'Refresh (Returns)' },
    { value: 'phone', label: 'Phone (Support)' },
    { value: 'clock', label: 'Clock (Fast)' },
    { value: 'creditCard', label: 'Credit Card (Payment)' },
    { value: 'check', label: 'Check (Quality)' },
    { value: 'star', label: 'Star (Rating)' },
    { value: 'gift', label: 'Gift (Offers)' },
  ];

  const addBadge = () => {
    const newBadge = {
      id: `badge-${Date.now()}`,
      icon: 'check',
      label: 'New Badge',
      description: '',
    };
    onChange([...badges, newBadge]);
  };

  const updateBadge = (index, field, value) => {
    const newBadges = [...badges];
    newBadges[index] = { ...newBadges[index], [field]: value };
    onChange(newBadges);
  };

  const removeBadge = (index) => {
    const newBadges = badges.filter((_, i) => i !== index);
    onChange(newBadges);
  };

  const moveBadge = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= badges.length) return;
    const newBadges = [...badges];
    [newBadges[index], newBadges[newIndex]] = [newBadges[newIndex], newBadges[index]];
    onChange(newBadges);
  };

  return (
    <div className="tb-slide-editor">
      {badges.map((badge, index) => (
        <div key={badge.id || index} className="tb-slide-editor__slide">
          <div className="tb-slide-editor__header">
            <span className="tb-slide-editor__number">Badge {index + 1}</span>
            <div className="tb-slide-editor__actions">
              <button
                type="button"
                className="tb-slide-editor__btn"
                onClick={() => moveBadge(index, 'up')}
                disabled={index === 0}
                title="Move up"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="tb-slide-editor__btn"
                onClick={() => moveBadge(index, 'down')}
                disabled={index === badges.length - 1}
                title="Move down"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                className="tb-slide-editor__btn tb-slide-editor__btn--danger"
                onClick={() => removeBadge(index)}
                title="Remove badge"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="tb-slide-editor__fields">
            <FieldGroup label="Icon">
              <select
                value={badge.icon || 'check'}
                onChange={e => updateBadge(index, 'icon', e.target.value)}
              >
                {iconOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FieldGroup>
            <FieldGroup label="Label">
              <input
                type="text"
                value={badge.label || ''}
                onChange={e => updateBadge(index, 'label', e.target.value)}
                placeholder="Badge label"
              />
            </FieldGroup>
            <FieldGroup label="Description (optional)">
              <input
                type="text"
                value={badge.description || ''}
                onChange={e => updateBadge(index, 'description', e.target.value)}
                placeholder="Short description"
              />
            </FieldGroup>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="tb-slide-editor__add"
        onClick={addBadge}
      >
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Badge
      </button>
    </div>
  );
}

// Slide Editor Component for Hero Slider
function SlideEditor({ slides = [], onChange }) {
  const addSlide = () => {
    const newSlide = {
      id: `slide-${Date.now()}`,
      title: 'New Slide',
      subtitle: '',
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      backgroundImage: '',
    };
    onChange([...slides, newSlide]);
  };

  const updateSlide = (index, field, value) => {
    const newSlides = [...slides];
    newSlides[index] = { ...newSlides[index], [field]: value };
    onChange(newSlides);
  };

  const removeSlide = (index) => {
    const newSlides = slides.filter((_, i) => i !== index);
    onChange(newSlides);
  };

  const moveSlide = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= slides.length) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
    onChange(newSlides);
  };

  return (
    <div className="tb-slide-editor">
      {slides.map((slide, index) => (
        <div key={slide.id || index} className="tb-slide-editor__slide">
          <div className="tb-slide-editor__header">
            <span className="tb-slide-editor__number">Slide {index + 1}</span>
            <div className="tb-slide-editor__actions">
              <button
                type="button"
                className="tb-slide-editor__btn"
                onClick={() => moveSlide(index, 'up')}
                disabled={index === 0}
                title="Move up"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="tb-slide-editor__btn"
                onClick={() => moveSlide(index, 'down')}
                disabled={index === slides.length - 1}
                title="Move down"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                className="tb-slide-editor__btn tb-slide-editor__btn--danger"
                onClick={() => removeSlide(index)}
                title="Remove slide"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="tb-slide-editor__fields">
            <FieldGroup label="Title">
              <input
                type="text"
                value={slide.title || ''}
                onChange={e => updateSlide(index, 'title', e.target.value)}
                placeholder="Slide title"
              />
            </FieldGroup>
            <FieldGroup label="Subtitle">
              <input
                type="text"
                value={slide.subtitle || ''}
                onChange={e => updateSlide(index, 'subtitle', e.target.value)}
                placeholder="Slide subtitle"
              />
            </FieldGroup>
            <FieldGroup label="Button Text">
              <input
                type="text"
                value={slide.buttonText || ''}
                onChange={e => updateSlide(index, 'buttonText', e.target.value)}
                placeholder="Shop Now"
              />
            </FieldGroup>
            <FieldGroup label="Button Link">
              <input
                type="text"
                value={slide.buttonLink || ''}
                onChange={e => updateSlide(index, 'buttonLink', e.target.value)}
                placeholder="/shop"
              />
            </FieldGroup>
            <ThemeImageField label="Background image" value={slide.backgroundImage || ''} onChange={value => updateSlide(index, 'backgroundImage', value)} />
          </div>
        </div>
      ))}
      <button
        type="button"
        className="tb-slide-editor__add"
        onClick={addSlide}
      >
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Slide
      </button>
    </div>
  );
}

export default SettingsPanel;
